import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoChevronForward, IoChevronDown } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import PaymentIcons from '../PaymentIcons';

interface FullFaqModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FaqCategory {
  key: string;
  title: string;
  description?: string;
}

interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  customComponent?: string;
}

interface SubjectFaq {
  title: string;
  qa?: { q: string; a: string | string[] }[];
  overview?: { title: string; paras: string[] };
  [key: string]: any;
}

const FullFaqModal: React.FC<FullFaqModalProps> = ({ isOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'ja';
  
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [items, setItems] = useState<FaqItem[]>([]);
  const [subjectFaqs, setSubjectFaqs] = useState<Record<string, SubjectFaq>>({});
  const [activeCategory, setActiveCategory] = useState<string>('service');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/data/faq_${lang}.json`);
        const data = await response.json();
        
        // Combine base categories with subject-specific ones
        const baseCategories = data.categories || [];
        const subjects = data.faq || {};
        setSubjectFaqs(subjects);

        const subjectCategories = Object.keys(subjects).map(key => ({
          key,
          title: subjects[key].page?.title || subjects[key].title || key,
          isSubject: true
        }));

        setCategories([...baseCategories, ...subjectCategories]);
        setItems(data.items || []);
        
        // If current activeCategory is no longer valid, reset to first
        if (![...baseCategories, ...subjectCategories].find(c => c.key === activeCategory)) {
          setActiveCategory(baseCategories[0]?.key || 'service');
        }
      } catch (error) {
        console.error('Failed to fetch FAQ data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, lang]);

  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const currentCategoryData = categories.find(c => c.key === activeCategory);
  const isSubjectCategory = !items.some(item => item.category === activeCategory) && subjectFaqs[activeCategory];

  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 lg:p-10">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-navy/60 backdrop-blur-sm"
        />

        {/* Modal Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-6xl bg-white md:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-slate-100 flex-shrink-0 md:rounded-t-[2.5rem]">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-navy">{t('lp.faq.title')}</h2>
              <p className="text-sm text-navy/50 font-medium mt-1">{lang === 'zh' ? '消除疑问与不安，安心地开始诊疗吧' : t('lp.faq.modal_subtitle', '疑問や不安を解消して、安心して診療を始めましょう')}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors text-navy/40 hover:text-navy"
            >
              <IoClose size={28} />
            </button>
          </div>

          <div className="flex-grow overflow-hidden flex flex-col md:flex-row">
            {/* Sidebar / Category List */}
            <div className="w-full md:w-72 border-r border-slate-100 bg-slate-50/50 flex-shrink-0">
              <div className="h-full overflow-y-auto px-4 py-6 md:px-6">
                <p className="hidden md:block text-xs font-bold text-navy/30 uppercase tracking-widest mb-4 px-2">
                  {lang === 'zh' ? '问题分类' : t('lp.faq.categories', 'カテゴリー')}
                </p>
                
                {/* Mobile horizontal scroll categories */}
                <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible no-scrollbar pb-4 md:pb-0">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setActiveCategory(cat.key)}
                      className={`flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-bold transition-all duration-200 whitespace-nowrap md:whitespace-normal group ${
                        activeCategory === cat.key
                          ? 'bg-white text-coral shadow-md border-coral/10'
                          : 'text-navy/60 hover:bg-white/80 hover:text-navy'
                      }`}
                    >
                      <span>{cat.title}</span>
                      <IoChevronForward 
                        size={14} 
                        className={`hidden md:block transition-transform duration-200 ${
                          activeCategory === cat.key ? 'translate-x-1 opacity-100' : 'opacity-0'
                        }`} 
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-grow overflow-y-auto bg-white px-6 md:px-12 py-8 md:py-10">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-coral/30 border-t-coral rounded-full animate-spin"></div>
                    <p className="text-sm font-bold text-navy/40">Loading...</p>
                  </div>
                </div>
              ) : (
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-navy">{currentCategoryData?.title}</h3>
                    {currentCategoryData?.description && (
                      <p className="text-navy/50 mt-2 font-medium">{currentCategoryData.description}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* General FAQ Items */}
                    {!isSubjectCategory && items
                      .filter(item => item.category === activeCategory)
                      .map((item) => (
                        <FaqAccordionItem 
                          key={item.id} 
                          item={item} 
                          isOpen={!!openItems[item.id]} 
                          onToggle={() => toggleItem(item.id)} 
                        />
                      ))}

                    {/* Subject-Specific FAQ Content */}
                    {isSubjectCategory && (
                      <div className="space-y-10">
                        {subjectFaqs[activeCategory].overview && (
                          <div className="rounded-2xl bg-sky-50 p-6 md:p-8 border border-sky-100">
                            <h4 className="text-lg font-bold text-sky-800 mb-4">{subjectFaqs[activeCategory]?.overview?.title}</h4>
                            <div className="space-y-3 text-sky-900/80 leading-relaxed font-medium">
                              {subjectFaqs[activeCategory]?.overview?.paras?.map((p: string, i: number) => (
                                <p key={i}>{p}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Special sections for things like mechanisms, etc, if they exist */}
                        {Object.keys(subjectFaqs[activeCategory]).map(key => {
                          if (['qa', 'overview', 'page', 'title'].includes(key)) return null;
                          const section = subjectFaqs[activeCategory][key];
                          if (typeof section !== 'object' || !section.title) return null;
                          
                          return (
                            <div key={key} className="space-y-4">
                              <h4 className="text-lg font-bold text-navy flex items-center gap-3">
                                <span className="w-1.5 h-6 bg-coral/30 rounded-full inline-block"></span>
                                {section.title}
                              </h4>
                              {section.paras && (
                                <div className="space-y-3 text-navy/70 leading-relaxed font-medium">
                                  {section.paras.map((p: string, i: number) => <p key={i}>{p}</p>)}
                                </div>
                              )}
                              {section.items && (
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {section.items.map((item: any, i: number) => (
                                    <li key={i} className="flex flex-col gap-2 p-4 rounded-xl bg-slate-50 border border-slate-100 text-sm font-bold text-navy/80">
                                      {typeof item === 'string' ? (
                                        <div className="flex items-start gap-3">
                                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0"></span>
                                          {item}
                                        </div>
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          <div className="flex items-start gap-3">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0"></span>
                                            <span className="text-coral-dark">{item.k}</span>
                                          </div>
                                          {item.v && (
                                            <div className="ml-4 flex flex-col gap-1 text-navy/70 font-medium whitespace-pre-line">
                                              {Array.isArray(item.v) ? item.v.join('\n') : item.v}
                                            </div>
                                          )}
                                          {item.note && (
                                            <div className="ml-4 text-xs text-navy/50 font-medium">
                                              {item.note}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {section.rules && (
                                <ul className="space-y-3">
                                  {section.rules.map((rule: string, i: number) => (
                                    <li key={i} className="flex items-start gap-3 text-navy/70 font-medium">
                                      <span className="mt-2.5 w-1 h-1 rounded-full bg-navy/30 flex-shrink-0"></span>
                                      {rule}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}

                        {/* Subject Q&A */}
                        {subjectFaqs[activeCategory].qa && (
                          <div className="space-y-4">
                            <h4 className="text-xl font-bold text-navy mb-6">
                              {lang === 'ja' ? 'よくある質問' : '常见问题'}
                            </h4>
                            {subjectFaqs[activeCategory]?.qa?.map((qa: any, i: number) => (
                              <FaqAccordionItem 
                                key={i} 
                                item={{
                                  id: `qa-${i}`,
                                  question: qa.q,
                                  answer: Array.isArray(qa.a) ? qa.a.join('\n') : qa.a
                                } as any} 
                                isOpen={!!openItems[`qa-${activeCategory}-${i}`]} 
                                onToggle={() => toggleItem(`qa-${activeCategory}-${i}`)} 
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const FaqAccordionItem = ({ item, isOpen, onToggle }: { item: FaqItem, isOpen: boolean, onToggle: () => void }) => {
  return (
    <div className={`rounded-2xl border transition-all duration-300 ${
      isOpen ? 'border-coral/20 bg-coral/5 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="text-base font-bold text-navy pr-4 leading-snug">
          {item.question}
        </span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
          isOpen ? 'bg-coral text-white rotate-180' : 'bg-slate-100 text-slate-400'
        }`}>
          <IoChevronDown size={14} />
        </div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        className="overflow-hidden"
      >
        <div className="px-5 pb-5 text-sm leading-relaxed text-navy/70 font-medium border-t border-coral/5 pt-4">
          <div className="whitespace-pre-line">
            {item.answer}
          </div>
          {item.customComponent === 'payment_icons' && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <PaymentIcons />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FullFaqModal;
